import { motion } from "framer-motion";
import { Zap, Layers, Award, HeartHandshake, Trophy, Star, GraduationCap, Video, Rocket, Gauge } from "lucide-react";

const HIGHLIGHTS = [
  { icon: Trophy, text: "NCAA Division III Indoor National Champion (200m)" },
  { icon: Star, text: "6× NCAA All-American" },
  { icon: GraduationCap, text: "Former Collegiate Head Cross Country & Track & Field Coach" },
  { icon: Gauge, text: "Individualized Speed Development Programs" },
  { icon: Video, text: "Sprint Video Analysis & Performance Feedback" },
  { icon: Rocket, text: "Acceleration & Maximum Velocity Training" },
];

const CARDS = [
  {
    icon: Zap,
    title: "Elite Sprint Mechanics",
    desc: "Teach athletes how to move efficiently, accelerate explosively, and maximize top-end speed.",
  },
  {
    icon: Layers,
    title: "Complete Athletic Development",
    desc: "Strength, power, mobility, agility, plyometrics, recovery, and movement quality.",
  },
  {
    icon: Award,
    title: "Proven Coaching",
    desc: "Built through years of collegiate coaching experience and developing athletes at multiple levels.",
  },
  {
    icon: HeartHandshake,
    title: "Relationships & Community",
    desc: "We build lasting relationships with athletes, coaches, and parents — developing confidence, character, and community. Others share knowledge; we help athletes become better people, too.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="py-16 md:py-36 bg-[#0a0a0a] border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
            The Standard
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Why Athletes Train Here
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            Every athlete receives intentional coaching built around proper sprint
            mechanics, explosive acceleration, maximum velocity development, and
            long-term athletic growth. Whether you're preparing for your next
            season, pursuing a college scholarship, or striving to reach your full
            athletic potential, every training session is designed with purpose and
            measurable progress.
          </p>
        </motion.div>

        {/* Coach credentials / highlights */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-16">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <motion.div
                key={h.text}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`highlight-${i}`}
                className="group bg-[#0a0a0a] hover:bg-black p-7 flex items-start gap-4 transition-colors duration-300"
              >
                <Icon size={24} className="text-white/80 shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-display font-bold uppercase tracking-tight text-lg leading-tight">
                  {h.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`why-card-${i}`}
                className="group bg-black border border-white/10 p-8 lg:p-9 transition-all duration-500 hover:-translate-y-2 hover:border-white/30"
              >
                <Icon size={32} className="text-white/80 mb-8" strokeWidth={1.5} />
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl lg:text-3xl mb-4 leading-none">
                  {c.title}
                </h3>
                <p className="text-white/55 leading-relaxed text-sm lg:text-base">{c.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
